import { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";

import "../styles/Map.css";
import { filter } from "d3";

const Maps = () => {

  const [mapData, setMapData] = useState([]);
  const [filteredmapData, setFilteredMapData] = useState([]);
  const [filterdata, setFilterData] = useState({});
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedQR, setSelectedQR] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");

  const baseURL = sessionStorage.getItem('baseURL');

  /* ── Helpers (logic unchanged) ────────────────────────────────────── */

  function formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${day} ${month} ${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
  }

  /* ── Data fetch ───────────────────────────────────────────────────── */

  useEffect(() => {
    axios.get(baseURL + `verification/fetchAllLocationDetails/all`)
      .then(res => setMapData(res.data));
  }, []);

  /* ── Build filter tree ────────────────────────────────────────────── */

  useEffect(() => {
    const map = {};
    filteredmapData.forEach(item => {
      const product = item.brand_name;
      const qr = item.uid;
      const batch = item.batch_no.toUpperCase();
      if (product) {
        if (!map[product]) map[product] = {};
        if (!map[product][qr]) map[product][qr] = new Set();
        if (batch !== 'CC') map[product][qr].add(batch);
      }
    });
    setFilterData(map);
  }, [filteredmapData]);

  /* ── Apply filters ────────────────────────────────────────────────── */

  useEffect(() => {
    let filtered = mapData;

    if (selectedDate) filtered = filtered.filter(item => {
      const fetched = new Date(item.created_at);
      let year = fetched.getFullYear();
      let month = fetched.getMonth() > 9 ? fetched.getMonth() + 1 : "0" + (fetched.getMonth() + 1);
      let date = fetched.getDate() > 9 ? fetched.getDate() : "0" + fetched.getDate();
      const formatedDate = year + "-" + month + "-" + date;
      return formatedDate == selectedDate
    });
    if (selectedProduct) filtered = filtered.filter(item => item.brand_name === selectedProduct);
    if (selectedQR) filtered = filtered.filter(item => item.uid === selectedQR);
    if (selectedBatch) filtered = filtered.filter(item => item.batch_no.toUpperCase() === selectedBatch);
    setFilteredMapData(filtered);
  }, [mapData, selectedDate, selectedProduct, selectedQR, selectedBatch]);

  /* ── Map icons (logic unchanged) ─────────────────────────────────── */

  const greenIcon = new L.Icon({
    iconUrl: process.env.PUBLIC_URL + "/assets/marker-icon-green.png",
    shadowUrl: process.env.PUBLIC_URL + "/assets/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41],
    popupAnchor: [1, -34], shadowSize: [41, 41],
  });

  const redIcon = new L.Icon({
    iconUrl: process.env.PUBLIC_URL + "/assets/marker-icon-orange.png",
    shadowUrl: process.env.PUBLIC_URL + "/assets/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41],
    popupAnchor: [1, -34], shadowSize: [41, 41],
  });

  /* ── Render ───────────────────────────────────────────────────────── */

  return (
    <div className="maps-container">

      {/* ── Filter bar ──────────────────────────────────────────────── */}
      <div className="map-filter-bar">
        <div className="map-filter-item">
          <label>Date</label>
          <input type="date" value={selectedDate} onChange={(e) => { setSelectedDate(e.target.value); setSelectedProduct("") }} />
        </div>
        <div className="map-filter-item">
          <label>Product Name</label>
          <select
            value={selectedProduct}
            onChange={(e) => { setSelectedProduct(e.target.value); setSelectedQR(""); setSelectedBatch(""); }}
          >
            <option value="">All Products</option>
            {Object.keys(filterdata).map(product => (
              <option key={product} value={product}>{product}</option>
            ))}
          </select>
        </div>

        {selectedProduct && (
          <div className="map-filter-item">
            <label>QR UID</label>
            <select
              value={selectedQR}
              onChange={(e) => { setSelectedQR(e.target.value); setSelectedBatch(""); }}
            >
              <option value="">All</option>
              {Object.keys(filterdata[selectedProduct]).sort().map(qr => (
                <option key={qr} value={qr}>{qr}</option>
              ))}
            </select>
          </div>
        )}

        {selectedQR && (
          <div className="map-filter-item">
            <label>Batch</label>
            <select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)}>
              <option value="">All</option>
              {Array.from(filterdata[selectedProduct][selectedQR]).map(batch => (
                <option key={batch} value={batch}>{batch}</option>
              ))}
            </select>
          </div>
        )}

        {/* Live count badge */}
        <div className="map-count-badge">
          <span className="map-count-number">{filteredmapData.filter(p => p.latitude && p.longitude).length}</span>
          <span className="map-count-label">verifications</span>
        </div>
      </div>

      {/* ── Map ─────────────────────────────────────────────────────── */}
      <MapContainer
        className="map-canvas"
        center={[20, 78]}
        zoom={4}
        maxZoom={20}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          noWrap={true}
          maxZoom={20}
          maxNativeZoom={18}
        />
        <MarkerClusterGroup>
          {filteredmapData
            .filter(p => p.latitude && p.longitude)
            .map(p => (
              <Marker
                key={p.verification_id}
                position={[parseFloat(p.latitude), parseFloat(p.longitude)]}
                icon={p.result === "1" ? greenIcon : redIcon}
              >
                <Popup>
                  <div className="map-popup">
                    <div className="map-popup-row"><span className="map-popup-label">Product</span><span>{p.brand_name}</span></div>
                    {p.result === "1" && (
                      <div className="map-popup-row"><span className="map-popup-label">Batch</span><span>{p.batch_no}</span></div>
                    )}
                    <div className={`map-popup-row result-row ${p.result === "1" ? "genuine" : "counterfeit"}`}>
                      <span className="map-popup-label">Result</span>
                      <span className="result-badge">{p.result === "1" ? "Verified" : "Not-Verified"}</span>
                    </div>
                    <div className="map-popup-row"><span className="map-popup-label">State</span><span>{p.state}</span></div>
                    <div className="map-popup-row"><span className="map-popup-label">District</span><span>{p.district}</span></div>
                    <div className="map-popup-row"><span className="map-popup-label">IP</span><span>{p.ip_address}</span></div>
                    <div className="map-popup-row"><span className="map-popup-label">Time</span><span>{formatDate(new Date(p.created_at))}</span></div>
                  </div>
                </Popup>
              </Marker>
            ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default Maps;